import React, { useEffect, useState } from "react";
import { api, errMsg } from "../../lib/api";
import { Card, Btn } from "../../components/UI";
import { Zap, Loader2, Undo2, Check, History, ClipboardList } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  applied: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  proposed: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  reverted: "text-slate-400 border-white/15 bg-white/5",
  approved: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  dismissed: "text-slate-400 border-white/15 bg-white/5",
};

const fmtVal = (field, v) => (field === "daily_budget" ? `$${Number(v).toLocaleString()}` : String(v));

export const AutopilotActivity = () => {
  const [changes, setChanges] = useState(null);
  const [recHistory, setRecHistory] = useState(null);
  const [running, setRunning] = useState(false);
  const [acting, setActing] = useState("");

  const load = () => {
    api.get("/autopilot/changes").then((r) => setChanges(r.data.changes || [])).catch(() => setChanges([]));
    api.get("/simple/recommendations/history").then((r) => setRecHistory(r.data.history || [])).catch(() => setRecHistory([]));
  };
  useEffect(load, []);

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/autopilot/run");
      toast.success(`${data.applied} applied · ${data.proposed} proposed — ${data.note}`);
      load();
    } catch (e) { toast.error(errMsg(e, "Optimization run failed")); }
    finally { setRunning(false); }
  };

  const act = async (ch, action) => {
    setActing(ch.id + action);
    try {
      await api.post(`/autopilot/changes/${ch.id}/${action}`);
      toast.success(action === "revert" ? "Change reverted — original value restored" : "Change approved & applied");
      load();
    } catch (e) { toast.error(errMsg(e, "Action failed")); }
    finally { setActing(""); }
  };

  return (
    <div className="mt-10 space-y-6" data-testid="autopilot-activity">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2">
            <Zap className="w-4 h-4 text-cyan-300" /> Run optimization
          </h2>
          <p className="text-xs text-slate-500 mt-1">AI reviews live delivery and acts within your limits — every change logged and reversible with a single tap.</p>
        </div>
        <Btn onClick={run} disabled={running} data-testid="autopilot-run-btn">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Run optimization
        </Btn>
      </div>

      <Card padded={false} data-testid="autopilot-change-history">
        <div className="px-5 py-3.5 border-b border-white/5 flex items-center gap-2">
          <History className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-base font-bold">Change history</h3>
        </div>
        {changes === null ? (
          <div className="flex justify-center py-8"><Loader2 className="w-4 h-4 animate-spin text-cyan-300" /></div>
        ) : changes.length === 0 ? (
          <div className="text-center py-10 px-6" data-testid="autopilot-changes-empty">
            <Zap className="w-6 h-6 text-slate-600 mx-auto mb-2" />
            <div className="text-sm text-slate-400 font-medium">No optimizations yet</div>
            <p className="text-xs text-slate-500 mt-1">Run optimization to see AI-applied and proposed changes here — each one reversible with a single tap.</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {changes.map((ch) => (
              <div key={ch.id} className="px-5 py-3.5 flex items-center gap-4" data-testid={`autopilot-change-${ch.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white">{ch.kind_label}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {ch.campaign_name}{ch.field ? ` · ${fmtVal(ch.field, ch.old_value)} → ${fmtVal(ch.field, ch.new_value)}` : ""}
                  </div>
                  <div className="text-[10px] text-slate-600 mt-0.5 truncate">{ch.reason}</div>
                </div>
                <span className={`shrink-0 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLE[ch.status] || ""}`}>
                  {ch.status}
                </span>
                {ch.status === "proposed" && (
                  <button onClick={() => act(ch, "approve")} disabled={!!acting} data-testid={`autopilot-approve-${ch.id}`}
                    className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5">
                    {acting === ch.id + "approve" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Approve
                  </button>
                )}
                {ch.status === "applied" && ch.revertible && (
                  <button onClick={() => act(ch, "revert")} disabled={!!acting} data-testid={`autopilot-revert-${ch.id}`}
                    className="shrink-0 text-xs px-3 py-1.5 rounded-full border border-white/15 text-slate-300 hover:border-rose-400/40 hover:text-rose-300 transition-colors disabled:opacity-50 inline-flex items-center gap-1.5">
                    {acting === ch.id + "revert" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Revert
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card padded={false} data-testid="recommendation-history">
        <div className="px-5 py-3.5 border-b border-white/5 flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-base font-bold">Recommendation decisions</h3>
        </div>
        {recHistory === null ? (
          <div className="flex justify-center py-8"><Loader2 className="w-4 h-4 animate-spin text-cyan-300" /></div>
        ) : recHistory.length === 0 ? (
          <p className="text-sm text-slate-500 px-5 py-6" data-testid="rec-history-empty">No decisions yet — approvals and dismissals from your Home screen land here.</p>
        ) : (
          <div className="divide-y divide-white/5">
            {recHistory.map((r) => (
              <div key={r.id} className="px-5 py-3 flex items-center gap-4" data-testid={`rec-history-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{r.title}</div>
                  <div className="text-[10px] text-slate-500">
                    {r.decided_by || ""}{r.decided_at ? ` · ${new Date(r.decided_at).toLocaleString()}` : ""}
                  </div>
                </div>
                <span className={`shrink-0 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_STYLE[r.status] || ""}`}>
                  {r.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
